import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, Search, Save, Plus, ChevronLeft, Trash2, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";

export function AdminUsers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const runSearch = useCallback(async (term) => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/users?q=${encodeURIComponent(term)}&limit=100`); setItems(data.items || []); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { runSearch(""); }, [runSearch]); // initial load
   
  if (selectedId) return <UserDetail userId={selectedId} onBack={() => { setSelectedId(null); runSearch(q); }} />;

  return (
    <div data-testid="admin-users">
      <div className="dms-label mb-3">Users</div>
      <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">User management</h1>
      <form onSubmit={(e) => { e.preventDefault(); runSearch(q); }} className="mt-6 dms-card p-3 flex gap-2 items-center">
        <Search className="w-4 h-4 text-[#94A3B8] ml-2" />
        <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Email or name…" className="dms-input border-0 shadow-none focus:ring-0" />
        <button className="dms-btn-primary text-[13px] py-2 px-4">Search</button>
      </form>
      <div className="mt-4 dms-card overflow-x-auto">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="text-left text-[11px] tracking-[0.2em] uppercase font-bold text-[#64748B] border-b border-[#E2E8F0]">
              <th className="p-3">Email</th><th className="p-3">Name</th><th className="p-3">Tier</th><th className="p-3">Status</th><th className="p-3">Queries</th><th className="p-3">Joined</th><th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="p-6 text-center text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr> :
             items.map((u) => (
              <tr key={u.user_id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]">
                <td className="p-3"><div className="font-medium">{u.email}</div></td>
                <td className="p-3 text-[#475569]">{u.name}</td>
                <td className="p-3"><span className="dms-badge">{u.subscription?.tier || "-"}</span></td>
                <td className="p-3 text-[#64748B]">{u.subscription?.status}</td>
                <td className="p-3 text-[#475569]">{u.queries_used || 0} / {u.subscription?.monthly_cap || "-"}</td>
                <td className="p-3 text-[#94A3B8]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</td>
                <td className="p-3"><button onClick={() => setSelectedId(u.user_id)} data-testid={`admin-user-open-${u.user_id}`} className="text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342]">Open →</button></td>
              </tr>
             ))}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#94A3B8]">No users.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UserDetail({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [brainDraft, setBrainDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/users/${userId}`);
    setData(data);
    setBrainDraft(JSON.stringify(data.brain || {}, null, 2));
    setDraft({});
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>;

  const u = data.user;
  const save = async () => {
    if (Object.keys(draft).length === 0) { toast.info("Nothing to update"); return; }
    setBusy(true);
    try { await api.put(`/admin/users/${userId}`, draft); toast.success("User updated"); await load(); }
    catch (e) { toast.error("Update failed"); } finally { setBusy(false); }
  };
  const saveBrain = async () => {
    try {
      const parsed = JSON.parse(brainDraft);
      await api.put(`/admin/users/${userId}/brain`, { brain: parsed });
      toast.success("Brain saved");
    } catch { toast.error("Invalid JSON"); }
  };

  return (
    <div data-testid="admin-user-detail">
      <button onClick={onBack} className="text-[13px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4"><ChevronLeft className="w-4 h-4" /> All users</button>
      <div className="dms-card p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="dms-label">User</div>
            <h2 className="font-heading font-bold text-xl text-[#0A2342]">{u.name || "-"}</h2>
            <div className="text-sm text-[#64748B]">{u.email}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="dms-badge">{u.subscription?.tier}</span>
              <span className="dms-badge">{u.subscription?.status}</span>
              {u.is_admin && <span className="dms-badge bg-[#0A2342] text-[#4FD1C5]">admin</span>}
              {data.raw_user?.email_verified
                ? <span className="dms-badge bg-[#DCFCE7] text-[#166534]">email verified</span>
                : <span className="dms-badge bg-[#FEF3C7] text-[#92400E]">email unverified</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#64748B]">Queries</div>
            <div className="font-heading font-bold text-2xl text-[#0A2342]">{u.subscription?.queries_used || 0} / {u.subscription?.monthly_cap || "-"}</div>
            <div className="text-[11px] text-[#94A3B8] mt-1">+{u.subscription?.extra_queries || 0} extra</div>
          </div>
        </div>
      </div>

      <div className="mt-5 dms-card p-5 sm:p-6">
        <div className="dms-label mb-4">Grant / adjust access</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="dms-label block mb-2">Set plan tier</label>
            <select data-testid="admin-set-tier" className="dms-input" value={draft.plan_tier ?? ""} onChange={(e) => setDraft({ ...draft, plan_tier: e.target.value || null })}>
              <option value="">- no change -</option>
              <option value="basic">basic</option>
              <option value="pro">pro</option>
              <option value="pro_unlimited">pro_unlimited</option>
              <option value="consulting">consulting</option>
              <option value="none">none</option>
            </select>
          </div>
          <div>
            <label className="dms-label block mb-2">Grant months free (comp)</label>
            <input data-testid="admin-grant-months" type="number" min={0} max={36} className="dms-input" value={draft.grant_months ?? ""} onChange={(e) => setDraft({ ...draft, grant_months: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 3" />
          </div>
          <div>
            <label className="dms-label block mb-2">Extend trial (days)</label>
            <input data-testid="admin-extend-trial" type="number" min={0} max={90} className="dms-input" value={draft.extend_trial_days ?? ""} onChange={(e) => setDraft({ ...draft, extend_trial_days: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 7" />
          </div>
          <div>
            <label className="dms-label block mb-2">Add extra queries</label>
            <input data-testid="admin-extra-queries" type="number" min={0} max={10000} className="dms-input" value={draft.extra_queries_add ?? ""} onChange={(e) => setDraft({ ...draft, extra_queries_add: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 100" />
          </div>
          <div>
            <label className="dms-label block mb-2">Add bonus Strategy Credits</label>
            <input data-testid="admin-bonus-credits" type="number" min={-1000} max={10000} className="dms-input" value={draft.bonus_credits_add ?? ""} onChange={(e) => setDraft({ ...draft, bonus_credits_add: e.target.value ? Number(e.target.value) : null })} placeholder="e.g. 50 (or -20 to deduct)" />
          </div>
          <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={!!draft.reset_usage} onChange={(e) => setDraft({ ...draft, reset_usage: e.target.checked })} /> Reset usage to 0</label>
            <label className="flex items-center gap-2 text-[13.5px]"><input data-testid="admin-reset-credits" type="checkbox" checked={!!draft.reset_credits} onChange={(e) => setDraft({ ...draft, reset_credits: e.target.checked })} /> Reset credits used to 0</label>
            <label className="flex items-center gap-2 text-[13.5px]"><input data-testid="admin-unlimited-credits" type="checkbox" checked={draft.unlimited_credits ?? !!data.raw_user.unlimited_credits} onChange={(e) => setDraft({ ...draft, unlimited_credits: e.target.checked })} /> Unlimited credits</label>
            <label className="flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={draft.is_admin ?? u.is_admin} onChange={(e) => setDraft({ ...draft, is_admin: e.target.checked })} /> Admin</label>
            <label className="flex items-center gap-2 text-[13.5px]"><input type="checkbox" checked={draft.suspended ?? !!data.raw_user.suspended} onChange={(e) => setDraft({ ...draft, suspended: e.target.checked })} /> Suspended</label>
            <label className="flex items-center gap-2 text-[13.5px]" title="Manually mark email as verified (use if the user's verification link was blocked by a corporate email filter)."><input data-testid="admin-email-verified" type="checkbox" checked={draft.email_verified ?? !!data.raw_user.email_verified} onChange={(e) => setDraft({ ...draft, email_verified: e.target.checked })} /> Email verified</label>
          </div>
          <div className="sm:col-span-2">
            <label className="dms-label block mb-2">Admin notes</label>
            <textarea className="dms-textarea" rows={3} value={draft.notes ?? data.raw_user.admin_notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button onClick={save} disabled={busy} data-testid="admin-user-save" className="dms-btn-primary"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Apply changes"}</button>
        </div>
      </div>

      {/* Dangerous actions */}
      <div className="mt-5 dms-card p-5 sm:p-6 border-[#FCA5A5] bg-[#FEF2F2]">
        <div className="dms-label mb-3 text-[#B91C1C]">Dangerous actions</div>
        <div className="flex flex-wrap gap-3 items-center">
          <button
            data-testid="admin-reset-password"
            onClick={async () => {
              if (!window.confirm("Generate a new temporary password and email it to this user?")) return;
              try {
                const { data } = await api.post(`/admin/users/${userId}/reset-password`);
                await navigator.clipboard.writeText(data.temporary_password).catch(() => {});
                toast.success(`Temp password: ${data.temporary_password} (copied). Email ${data.email_sent ? "sent" : "failed - share manually"}.`, { duration: 20000 });
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Reset failed");
              }
            }}
            className="dms-btn-ghost"><KeyRound className="w-4 h-4" /> Reset password</button>
          <button
            data-testid="admin-copy-user-id"
            onClick={() => { navigator.clipboard.writeText(u.user_id); toast.success("User ID copied"); }}
            className="dms-btn-ghost"><Copy className="w-4 h-4" /> Copy user id</button>
          <div className="flex-1" />
          <button
            data-testid="admin-delete-user"
            onClick={async () => {
              const first = window.confirm(`Soft-delete ${u.email}?\n\nThe user will be suspended and their email replaced with a placeholder. Data preserved for audit.\n\nClick Cancel then use "Hard delete" if you want to permanently remove all their data.`);
              if (!first) return;
              try { await api.delete(`/admin/users/${userId}`); toast.success("User soft-deleted"); onBack(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
            }}
            className="dms-btn-ghost text-[#B91C1C] border-[#FCA5A5] hover:bg-[#FEE2E2]"><Trash2 className="w-4 h-4" /> Soft delete</button>
          <button
            data-testid="admin-hard-delete-user"
            onClick={async () => {
              const confirm1 = window.prompt(`HARD DELETE ${u.email}?\n\nThis PERMANENTLY removes the user + Dive Brain + all vault items + bug reports.\n\nType the user's email to confirm:`);
              if (confirm1 !== u.email) { if (confirm1 !== null) toast.error("Email did not match. Cancelled."); return; }
              try { await api.delete(`/admin/users/${userId}?hard=true`); toast.success("User permanently deleted"); onBack(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
            }}
            className="dms-btn-ghost text-white bg-[#B91C1C] border-[#B91C1C] hover:bg-[#991B1B]"><Trash2 className="w-4 h-4" /> Hard delete</button>
        </div>
      </div>

      {/* Dive Brain editor */}
      <div className="mt-5 dms-card p-5 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="dms-label">Dive Brain (JSON)</div>
          <button onClick={saveBrain} data-testid="admin-save-brain" className="dms-btn-ghost text-[13px] py-2"><Save className="w-3.5 h-3.5" /> Save brain</button>
        </div>
        <textarea className="dms-textarea font-mono text-[12.5px]" rows={14} value={brainDraft} onChange={(e) => setBrainDraft(e.target.value)} />
      </div>

      {/* Vault snippet */}
      <div className="mt-5 dms-card p-5 sm:p-6">
        <div className="dms-label mb-3">Recent vault ({data.vault_count} total)</div>
        <div className="space-y-2">
          {data.recent_vault.map((v) => (
            <div key={v.id} className="flex items-center justify-between text-[13px] border-b border-[#E2E8F0] last:border-0 py-2">
              <div><span className="font-semibold text-[#0A2342]">{v.title}</span><span className="ml-2 text-[#94A3B8]">[{v.category}]</span></div>
              <div className="text-[#94A3B8] text-xs">{new Date(v.created_at).toLocaleString()}</div>
            </div>
          ))}
          {data.recent_vault.length === 0 && <div className="text-[#94A3B8] text-sm">No items.</div>}
        </div>
      </div>

      {/* Transactions */}
      <div className="mt-5 dms-card p-5 sm:p-6">
        <div className="dms-label mb-3">Recent transactions</div>
        <div className="space-y-2">
          {data.transactions.map((t) => (
            <div key={t.session_id} className="flex items-center justify-between text-[13px] border-b border-[#E2E8F0] last:border-0 py-2">
              <div>{t.kind} · €{t.amount} · <span className="dms-badge">{t.payment_status}</span></div>
              <div className="text-[#94A3B8] text-xs">{new Date(t.created_at).toLocaleString()}</div>
            </div>
          ))}
          {data.transactions.length === 0 && <div className="text-[#94A3B8] text-sm">No transactions.</div>}
        </div>
      </div>
    </div>
  );
}

export default AdminUsers;
